import { Breadcrumb, Layout, Spin, Table, Tag, Button, Input, Select, Row, Col, Card, message, Space, Popconfirm, Modal, Form } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { useEffect, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import { Link } from 'react-router-dom';
import { ClearOutlined, DeleteOutlined, EditOutlined, FilterOutlined, PlusOutlined, SearchOutlined, PrinterOutlined, BookOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { APPBACKGROUND_COLOR } from '../appsettings';
import { PDFViewer } from '@react-pdf/renderer';
import InventoryItemReportPDF from './inventory_item_report-pdf';

const { Option } = Select;

const initialFilters = {
    type: 'all',
    category: 'all',
    status: 'all',
    search: '',
};

const Inventory = () => {
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(initialFilters);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const USERID = localStorage.getItem('USERID');
    const [categories, setCategories] = useState([]);
    const [types, setTypes] = useState([]);

    const [printPreview, setPrintPreview] = useState(false);
    const [printData, setPrintData] = useState(null);
    const [printingItemId, setPrintingItemId] = useState(null);

    /** Fetch inventory list */
    const fetchInventory = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                ...filters,
            });

            const response = await axiosClient.get(`/inventory/get_all_inventory.php?${params.toString()}`);
            const result = response.data;

            if (result.status === 'success') {
                setData(result.data);
                setPagination({
                    current: result.page,
                    pageSize: result.pageSize,
                    total: result.total,
                });
            } else {
                message.error(result.message || 'Failed to fetch inventory.');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            message.error(error.response?.data?.message || 'Network error.');
        } finally {
            setLoading(false);
        }
    };

    /** Fetch dropdown types */
    const fetchCategories = async () => {
        try {
            const response = await axiosClient.get('/master/category/get_all_categories_dropdown.php');
            const result = response.data;

            if (result.status === 'success') {
                setCategories(result.data);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error(error);
            message.error('Network error.');
        }
    };

    /** Fetch dropdown categories */
    const fetchTypes = async () => {
        try {
            const response = await axiosClient.get('/master/type/get_all_types_dropdown.php');
            const result = response.data;

            if (result.status === 'success') {
                setTypes(result.data);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error(error);
            message.error('Network error.');
        }
    };

    /** Print handling */
    const handlePrint = async (record) => {
        setPrintingItemId(record.id);

        try {
            const itemResponse = await axiosClient.get(`/inventory/get_inventory_item_by_id_with_records.php?id=${record.id}`);
            const itemResult = itemResponse.data;

            let itemDetails = record;
            let serviceRecords = [];

            if (itemResult.status === 'success') {
                itemDetails = itemResult.data;
                if (Array.isArray(itemDetails.record)) serviceRecords = itemDetails.record;
            }

            setPrintData({ item: itemDetails, records: serviceRecords });
            setPrintPreview(true);
        } catch (error) {
            console.error(error);
            message.error('Failed to prepare print data.');
        } finally {
            setPrintingItemId(null);
        }
    };

    /** Load dropdowns on mount */
    useEffect(() => {
        fetchCategories();
        fetchTypes();
        fetchInventory(1, pagination.pageSize); // load initial data
    }, []);

    /** Table pagination handlers */
    const handleTableChange = (newPagination) => {
        fetchInventory(newPagination.current, newPagination.pageSize);
    };

    /** Clear filter */
    const clearFilters = () => {
        form.resetFields();
        setFilters(initialFilters);
        setPagination({ current: 1, pageSize: 10, total: 0 });
        fetchInventory(1, 10);
    };

    /** Delete item */
    const handleDelete = async (id) => {
        try {
            const response = await axiosClient.delete(`/inventory/delete_from_inventory.php?id=${id}`, {
                data: { id, by: USERID }
            });

            const result = response.data;
            if (result.status === 'success') {
                message.success(result.message);
                fetchInventory(1, pagination.pageSize);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error(error);
            message.error('Network error.');
        }
    };

    /** Table Columns */
    const columns = [
        { title: 'Item Code', dataIndex: 'item_code' },
        { title: 'Name', dataIndex: 'name' },
        { title: 'Type', dataIndex: 'type' },
        { title: 'Category', dataIndex: 'category' },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Tag color={status ? 'green' : 'red'}>
                    {status ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            ),
        },
        {
            title: 'Register Date',
            dataIndex: 'register_date',
            render: (date) => date ? dayjs(date).format('DD MMM YYYY hh:mm A') : 'N/A',
        },
        {
            title: 'Action',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<PrinterOutlined />}
                        size="small"
                        type="default"
                        loading={printingItemId === record.id}
                        onClick={() => handlePrint(record)}
                    />

                    <Button
                        style={{ backgroundColor: "#FFC107", borderColor: "#FFC107", color: "white" }}
                        type="primary"
                        size="small"
                    >
                        <Link to={`/inventory/edit/${record.id}`}>
                            <EditOutlined />
                        </Link>
                    </Button>

                    <Popconfirm
                        title="Are you sure to delete this item?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button icon={<DeleteOutlined />} type="primary" danger size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Spin spinning={loading} tip="Loading Inventory...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />

                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item><Link to="/inventory/itemList">Inventory</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Item List</Breadcrumb.Item>
                    </Breadcrumb>

                    {/* FILTER CARD */}
                    <Card
                        style={{ marginBottom: "15px" }}
                        title="Filter"
                        extra={
                            <Row style={{ float: "right" }} gutter={[10, 10]} align="middle">
                                <Col>
                                    <Link to="/inventory/records/create">
                                        <Button icon={<BookOutlined />} type="primary">Add A Record</Button>
                                    </Link>
                                </Col>
                                <Col>
                                    <Link to="/inventory/add">
                                        <Button icon={<PlusOutlined />} type="primary">Add Item</Button>
                                    </Link>
                                </Col>
                            </Row>
                        }
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={initialFilters}
                            onValuesChange={(_, allValues) => setFilters(allValues)}
                            onFinish={() => fetchInventory(1, pagination.pageSize)}
                        >
                            <Row gutter={[10, 10]} align="middle">

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="search" label="Search">
                                        <Input
                                            placeholder="Search by Code or Name"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="type" label="Type">
                                        <Select>
                                            <Option value="all">All Type</Option>
                                            {types.map((type) => (
                                                <Option key={type.id} value={type.value}>{type.value}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="category" label="Category">
                                        <Select>
                                            <Option value="all">All Category</Option>
                                            {categories.map((cat) => (
                                                <Option key={cat.id} value={cat.value}>{cat.value}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="status" label="Status">
                                        <Select>
                                            <Option value="all">All Status</Option>
                                            <Option value="active">Active</Option>
                                            <Option value="inactive">Inactive</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={3}>
                                    <Button type="primary" block icon={<FilterOutlined />} htmlType="submit">
                                        Filter
                                    </Button>
                                </Col>

                                <Col xs={12} sm={6} md={3}>
                                    <Button block icon={<ClearOutlined />} onClick={clearFilters}>
                                        Clear
                                    </Button>
                                </Col>

                            </Row>
                        </Form>
                    </Card>

                    {/* TABLE */}
                    <Card title="Inventory">
                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey="id"
                            pagination={pagination}
                            onChange={handleTableChange}
                            bordered
                            size="small"
                            scroll={{ x: 800 }}
                        />
                    </Card>
                </Content>

                <Footerdiv />
            </Layout>

            {/* PRINT PREVIEW MODAL */}
            <Modal
                title="Inventory Item Print Preview"
                open={printPreview}
                onCancel={() => setPrintPreview(false)}
                footer={null}
                width={1000}
                centered
                destroyOnHidden
            >
                {printData && (
                    <PDFViewer width="100%" height="500px" showToolbar>
                        <InventoryItemReportPDF item={printData.item} records={printData.records} />
                    </PDFViewer>
                )}
            </Modal>
        </Spin>
    );
};

export default Inventory;
